import type {
  InnerWall,
  ProductInstance,
  Vec2,
  WallSide,
} from '../shared/types';
import { doorGlassSegments } from './glass';
import { wallThicknessOf } from './dent';

type Span = [number, number];

/** Subtract a set of openings from a full span, returning the remaining solid spans. */
function subtractOpenings(full: Span, openings: Span[]): Span[] {
  const sorted = openings
    .map((o) => [Math.min(o[0], o[1]), Math.max(o[0], o[1])] as Span)
    .filter((o) => o[1] > full[0] && o[0] < full[1])
    .sort((a, b) => a[0] - b[0]);
  const spans: Span[] = [];
  let cursor = full[0];
  for (const [a, b] of sorted) {
    const start = Math.max(a, full[0]);
    if (start - cursor > 1) spans.push([cursor, start]);
    cursor = Math.max(cursor, Math.min(b, full[1]));
  }
  if (full[1] - cursor > 1) spans.push([cursor, full[1]]);
  return spans;
}

/** A wall rectangle (outer edge to inner edge) over a span on the given side. */
function wallRect(side: WallSide, s: number, e: number, hw: number, hd: number, WALL: number): Vec2[] {
  if (side === 'front') {
    return [{ x: s, y: -hd }, { x: e, y: -hd }, { x: e, y: -hd + WALL }, { x: s, y: -hd + WALL }];
  }
  if (side === 'back') {
    return [{ x: s, y: hd }, { x: e, y: hd }, { x: e, y: hd - WALL }, { x: s, y: hd - WALL }];
  }
  if (side === 'left') {
    return [{ x: -hw, y: s }, { x: -hw, y: e }, { x: -hw + WALL, y: e }, { x: -hw + WALL, y: s }];
  }
  return [{ x: hw, y: s }, { x: hw, y: e }, { x: hw - WALL, y: e }, { x: hw - WALL, y: s }];
}

/** An axis-aligned wall band rectangle from (x0,y0) to (x1,y1). */
function band(x0: number, y0: number, x1: number, y1: number): Vec2[] {
  return [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
}

/** Local-frame rectangle of an interior wall: a WALL-thick band starting at the
 *  outer face of its wall (so it merges with it) and running inward by `length`. */
export function innerWallBand(inst: ProductInstance, iw: InnerWall): Vec2[] {
  const hw = inst.width / 2, hd = inst.depth / 2, h = wallThicknessOf(inst) / 2;
  const len = Math.max(0, iw.length);
  if (iw.wall === 'front') return band(iw.pos - h, -hd, iw.pos + h, -hd + len);
  if (iw.wall === 'back') return band(iw.pos - h, hd - len, iw.pos + h, hd);
  if (iw.wall === 'left') return band(-hw, iw.pos - h, -hw + len, iw.pos + h);
  return band(hw - len, iw.pos - h, hw, iw.pos + h); // right
}

/** Index of the interior wall hit by a local point (with margin), or -1. */
export function innerWallHitLocal(inst: ProductInstance, p: Vec2, margin: number): number {
  const arr = inst.innerWalls || [];
  for (let i = arr.length - 1; i >= 0; i--) {
    const b = innerWallBand(inst, arr[i]);
    const xs = b.map((q) => q.x), ys = b.map((q) => q.y);
    if (p.x >= Math.min(...xs) - margin && p.x <= Math.max(...xs) + margin &&
        p.y >= Math.min(...ys) - margin && p.y <= Math.max(...ys) + margin) return i;
  }
  return -1;
}

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

function boxOf(poly: Vec2[]): Box {
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/** Remove `covered` spans from [a,b], returning the parts that remain. */
function remainingSpans(a: number, b: number, covered: Span[]): Span[] {
  const sorted = covered
    .map((c) => [Math.min(c[0], c[1]), Math.max(c[0], c[1])] as Span)
    .filter((c) => c[1] > a + 1e-6 && c[0] < b - 1e-6)
    .sort((p, q) => p[0] - q[0]);
  const out: Span[] = [];
  let cursor = a;
  for (const [s, e] of sorted) {
    if (s - cursor > 1e-6) out.push([cursor, Math.min(s, b)]);
    cursor = Math.max(cursor, e);
  }
  if (b - cursor > 1e-6) out.push([cursor, b]);
  return out;
}

/**
 * The outline of the union of the wall bands: every band edge, minus the parts
 * that touch another band (interior seams), and minus what an earlier band's
 * edge on the same line, facing the same way, already gives (two bands
 * overlapping at a corner share their outer faces there) — so each stretch is
 * given once. Stroking only these segments makes the walls — including the
 * returns around dents — join seamlessly at corners instead of showing the
 * internal edges where bands overlap or butt together.
 */
export function wallBoundaryEdges(bands: Vec2[][]): Array<[Vec2, Vec2]> {
  const boxes = bands.map(boxOf);
  const eps = 0.5; // probe just outside an edge (mm); bands are >= WALL thick
  const segs: Array<[Vec2, Vec2]> = [];
  boxes.forEach((r, i) => {
    // Horizontal edges (bottom: outward -y, top: outward +y).
    for (const [y, ny] of [[r.y0, -1], [r.y1, 1]] as Array<[number, number]>) {
      const yo = y + eps * ny;
      const covered: Span[] = [];
      boxes.forEach((o, j) => {
        if (j === i) return;
        const twin = j < i && Math.abs((ny < 0 ? o.y0 : o.y1) - y) < 1e-6;
        if ((o.y0 < yo && yo < o.y1) || twin) {
          const s = Math.max(r.x0, o.x0);
          const e = Math.min(r.x1, o.x1);
          if (e - s > eps) covered.push([s, e]);
        }
      });
      for (const [s, e] of remainingSpans(r.x0, r.x1, covered)) segs.push([{ x: s, y }, { x: e, y }]);
    }
    // Vertical edges (left: outward -x, right: outward +x).
    for (const [x, nx] of [[r.x0, -1], [r.x1, 1]] as Array<[number, number]>) {
      const xo = x + eps * nx;
      const covered: Span[] = [];
      boxes.forEach((o, j) => {
        if (j === i) return;
        const twin = j < i && Math.abs((nx < 0 ? o.x0 : o.x1) - x) < 1e-6;
        if ((o.x0 < xo && xo < o.x1) || twin) {
          const s = Math.max(r.y0, o.y0);
          const e = Math.min(r.y1, o.y1);
          if (e - s > eps) covered.push([s, e]);
        }
      });
      for (const [s, e] of remainingSpans(r.y0, r.y1, covered)) segs.push([{ x, y: s }, { x, y: e }]);
    }
  });
  return segs;
}

/**
 * Whether a dent is glazed: glass on the wall the dent reaches covers the whole
 * mouth of the notch.
 */
/**
 * Build the solid wall segments of a sauna as closed polygons (local frame).
 * Each polygon shows the wall thickness; gaps left for the door and glass panes
 * naturally expose the jamb returns at their ends.
 */
export function wallPolygons(inst: ProductInstance, extraInner: InnerWall[] = [], windowOpenings = false): Vec2[][] {
  const hw = inst.width / 2;
  const hd = inst.depth / 2;
  const WALL = wallThicknessOf(inst);
  const openings: Record<WallSide, Span[]> = { front: [], back: [], left: [], right: [] };
  for (const g of inst.glass || []) openings[g.wall].push([g.start, g.end]);
  for (const door of doorGlassSegments(inst)) openings.front.push([door.start, door.end]);
  // Window openings gap the wall too (used by the 3D preview, which then fills the
  // sill below and architrave above); off by default so 2D/DXF stay unchanged.
  if (windowOpenings) for (const o of inst.openings || []) if (o.edge == null) openings[o.wall].push([o.start, o.end]);

  const polys: Vec2[][] = [];
  const sides: Array<{ side: WallSide; full: Span }> = [
    { side: 'front', full: [-hw, hw] },
    { side: 'back', full: [-hw, hw] },
    { side: 'left', full: [-hd, hd] },
    { side: 'right', full: [-hd, hd] },
  ];
  for (const { side, full } of sides) {
    for (const [s, e] of subtractOpenings(full, openings[side])) {
      polys.push(wallRect(side, s, e, hw, hd, WALL));
    }
  }

  // Interior walls: merge straight into the band set so they fill and outline
  // seamlessly with the outer walls.
  for (const iw of [...(inst.innerWalls || []), ...extraInner]) polys.push(innerWallBand(inst, iw));

  // Wind every band the same way (CCW) so callers can union-fill them with a
  // single nonzero path without opposite-wound overlaps cancelling to holes.
  for (const p of polys) if (signedArea(p) < 0) p.reverse();
  return polys;
}

/** Twice the signed area of a polygon; negative means clockwise. */
export function signedArea(pts: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a;
}
